/*
 * hsmdiagram.js
 *
 * Renders the PlantUML subset that CallCorp's HSM log generators emit, as SVG,
 * in the browser. No dependencies, no Java, no server round trip.
 *
 * Two generators feed this:
 *   Platform/Library/SearchAPI/SearchAPI.cs  CallLogsToPlantUml  (server side, ViewLog and the portal button)
 *   Vue/src/Services/plantUml.jsx                                (client side, live HSM debug tab)
 *
 * Between them the grammar is eleven line shapes. Everything they can emit is
 * handled here. Anything else lands in model.unknown so a new shape shows up
 * loudly instead of vanishing.
 *
 *   HsmDiagram.render(umlText, hostElement, {
 *       onLinkClick: function (href, label, ev) {},
 *       theme: 'light' | 'dark',
 *       compactIds: true
 *   })
 *
 * Ships as Vue/src/Services/hsmDiagram.js and Vue3/src/Services/hsmDiagram.js,
 * matching how plantUml.jsx is already duplicated across the two front ends.
 */
'use strict';


var SVGNS = 'http://www.w3.org/2000/svg';

// PlantUML takes HTML color names and #rrggbb. The generators only use the
// names below; anything else passes straight through to the browser.
var COLOR_ALIASES = {
    lightskyblue: '#87cefa',
    whitesmoke: '#f1f3f6',
    goldenrod: '#daa520',
    gold: '#ffd700',
    yellow: '#ffe9a8',
    green: '#2e9e4f',
    red: '#d64550',
    blue: '#3b7dd8',
    black: '#333333'
};

// Only the chrome is themed. Arrow and note colors carry meaning that the
// generators assign (green transition, gold event sent, red ignored, blue
// current state), so those come from the diagram and are never overridden.
var THEMES = {
    light: {
        ink: '#1f2430',
        lifeline: '#c7cdd6',
        boxFill: '#ffffff',
        boxStroke: '#aab2bf',
        activation: '#dfe5ee',
        activationStroke: '#9aa4b2',
        arrow: '#5b6472',
        offpage: '#8b93a1',
        link: '#1a63c7',
        noteStroke: 'rgba(0,0,0,.22)',
        noteInk: '#1f2430'
    },
    dark: {
        ink: '#dfe4ec',
        lifeline: '#39424f',
        boxFill: '#1c222b',
        boxStroke: '#48525f',
        activation: '#2b3542',
        activationStroke: '#5a6675',
        arrow: '#9aa5b4',
        offpage: '#6f7a89',
        link: '#78b0ff',
        noteStroke: 'rgba(255,255,255,.28)',
        noteInk: '#12161c'
    }
};

var THEME = {
    font: '13px "Segoe UI", system-ui, sans-serif',
    headFont: '600 13px "Segoe UI", system-ui, sans-serif'
};
for (var _k in THEMES.light) THEME[_k] = THEMES.light[_k];

function applyTheme(name) {
    var t = THEMES[name] || THEMES.light;
    for (var k in t) THEME[k] = t[k];
}

var GEOM = {
    headH: 34,
    headPadX: 14,
    colGap: 46,
    topPad: 16,
    rowGap: 8,
    msgH: 26,
    selfH: 44,
    notePadX: 12,
    notePadY: 8,
    noteLineH: 17,
    noteGap: 10,
    noteHexIn: 10,
    actW: 11,
    actIndent: 4,
    offpageX: 34,
    gutterL: 26,
    gutterR: 26,
    bottomPad: 20
};

/* ---------------------------------------------------------------- parsing */

// [[href{tooltip} label]] and <font color=x>...</font> are the only inline
// markup either generator produces. Everything else is literal text.
function parseInline(text) {
    var out = [];
    var re = /\[\[([^\s{\]]*)\{([^}]*)\}\s?([^\]]*)\]\]|<font color=([#\w]+)>(.*?)<\/font>/g;
    var pos = 0, m;
    while ((m = re.exec(text)) !== null) {
        if (m.index > pos) out.push({ text: text.slice(pos, m.index) });
        if (m[1] !== undefined) {
            // The server emits [[{tooltip} label]] with an EMPTY href when a log
            // row has no detail key. That is a tooltip, not a link, and it must
            // still show the tooltip rather than silently losing it.
            out.push({ text: m[3], href: m[1] || null, tip: m[2] || '' });
            if (m[1]) out[out.length - 1].href = safeDecode(m[1]);
        } else {
            out.push({ text: m[5], color: color(m[4]) });
        }
        pos = re.lastIndex;
    }
    if (pos < text.length) out.push({ text: text.slice(pos) });
    return out.filter(function (r) { return r.text !== ''; });
}

function safeDecode(s) {
    try { return decodeURIComponent(s); } catch (e) { return s; }
}

function color(name) {
    if (!name) return null;
    var k = String(name).replace(/^#/, '').toLowerCase();
    if (COLOR_ALIASES[k]) return COLOR_ALIASES[k];
    return /^[0-9a-f]{3,8}$/.test(k) ? '#' + k : k;
}

function parse(uml) {
    var lines = String(uml).split(/\r?\n/);
    var participants = [];
    var byId = {};
    var steps = [];
    var unknown = [];
    var i = 0;

    function want(id) {
        if (byId[id]) return byId[id];
        var p = { id: id, label: id, link: null, tip: null, order: participants.length };
        byId[id] = p;
        participants.push(p);
        return p;
    }

    while (i < lines.length) {
        var raw = lines[i], line = raw.trim(), m;
        i++;
        if (!line || line === '@startuml' || line === '@enduml') continue;

        // participant "Display Name" as Id [order N] [[link{tip} text]]
        // participant Id
        if ((m = line.match(/^participant\s+(?:"([^"]*)"\s+as\s+)?(\S+)(.*)$/))) {
            var p = want(m[2]);
            if (m[1]) p.label = m[1];
            var rest = m[3] || '';
            var om = rest.match(/\border\s+(-?\d+)/);
            if (om) p.order = parseInt(om[1], 10);
            var lm = rest.match(/\[\[([^\s{\]]*)\{([^}]*)\}\s?([^\]]*)\]\]/);
            if (lm) { p.link = safeDecode(lm[1]); p.tip = lm[2] || lm[3] || null; }
            continue;
        }

        if ((m = line.match(/^activate\s+(\S+)$/)))   { want(m[1]); steps.push({ t: 'activate',   id: m[1] }); continue; }
        if ((m = line.match(/^deactivate\s+(\S+)$/))) { want(m[1]); steps.push({ t: 'deactivate', id: m[1] }); continue; }

        // hnote over X #color ... endhnote   /   rnote right of X #color ... endrnote
        // The server generator always closes with endhnote even when it opened
        // rnote, so either terminator ends either shape.
        if ((m = line.match(/^([hr])note\s+(over|left of|right of)\s+([^\s#]+)\s*(#\S+)?\s*$/))) {
            var body = [];
            while (i < lines.length && !/^end[hr]note\s*$/.test(lines[i].trim())) { body.push(lines[i].trim()); i++; }
            i++;
            want(m[3]);
            steps.push({
                t: 'note', shape: m[1], side: m[2].replace(' ', ''), id: m[3],
                fill: color(m[4]) || '#fffbe6',
                rows: body.map(parseInline)
            });
            continue;
        }

        // [-> Target: label   (an event arriving from outside the diagram)
        if ((m = line.match(/^\[\s*(?:\[#(\w+)\])?-+>\s*([^:\s]+)\s*:\s*(.*)$/))) {
            want(m[2]);
            steps.push({ t: 'msg', from: null, to: m[2], color: color(m[1]) || THEME.offpage, label: parseInline(m[3]) });
            continue;
        }

        // From [#color]-> To: label   (To may be ] meaning off-page right)
        if ((m = line.match(/^(\S+?)\s*(?:\[#(\w+)\])?\s*-+>\s*([^:\s]+)\s*:\s*(.*)$/))) {
            want(m[1]);
            if (m[3] !== ']' && m[3] !== '[') want(m[3]);
            steps.push({
                t: 'msg', from: m[1],
                to: (m[3] === ']' || m[3] === '[') ? null : m[3],
                outbound: m[3] === ']',
                color: color(m[2]) || THEME.arrow,
                label: parseInline(m[4])
            });
            continue;
        }

        unknown.push({ line: i, text: raw });
    }

    participants.sort(function (a, b) { return a.order - b.order; });
    return { participants: participants, steps: steps, unknown: unknown };
}

/* ------------------------------------------------------------ measurement */

// measureText can hand back a non-finite width if the canvas is measured
// before the document has settled, which then poisons every downstream
// coordinate. Fall back to an estimate rather than emitting NaN geometry.
var _ctx = null;
function measure(text, font) {
    if (!_ctx) _ctx = document.createElement('canvas').getContext('2d');
    _ctx.font = font;
    var w = _ctx.measureText(text).width;
    return isFinite(w) ? w : String(text).length * 6.6;
}
function runsWidth(runs, font) {
    var w = 0;
    for (var i = 0; i < runs.length; i++) w += measure(runs[i].text, font);
    return w;
}
function noteWidth(s) {
    var w = 0;
    for (var i = 0; i < s.rows.length; i++) w = Math.max(w, runsWidth(s.rows[i], THEME.font));
    w += GEOM.notePadX * 2;
    if (s.shape === 'h') w += GEOM.noteHexIn * 2;
    return w;
}

/* ---------------------------------------------------------------- drawing */

function el(name, attrs, parent) {
    var n = document.createElementNS(SVGNS, name);
    for (var k in attrs) if (attrs[k] !== null && attrs[k] !== undefined) n.setAttribute(k, attrs[k]);
    if (parent) parent.appendChild(n);
    return n;
}

// One <text> built from <tspan> runs, with links as real anchors carrying
// real click handlers. No javascript: URL hack, and the text stays selectable.
function drawRuns(parent, runs, x, y, font, fill, anchor, onLinkClick, linkColor) {
    var t = el('text', { x: x, y: y, 'text-anchor': anchor || 'start', fill: fill || THEME.ink }, parent);
    t.style.font = font;
    runs.forEach(function (r) {
        var host = t;
        if (r.href) {
            var a = el('a', { 'class': 'hsm-link' }, t);
            a.setAttribute('href', r.href);
            a.style.cursor = 'pointer';
            a.addEventListener('click', function (ev) {
                ev.preventDefault();
                if (onLinkClick) onLinkClick(r.href, r.text, ev);
            });
            host = a;
        }
        var ts = el('tspan', { fill: r.color || (r.href ? (linkColor || THEME.link) : null) }, host);
        if (r.href) ts.style.textDecoration = 'underline';
        ts.textContent = r.text;
        // A tooltip with no href hangs off the tspan itself, so it survives.
        if (r.tip) { var ti = el('title', {}, r.href ? host : ts); ti.textContent = r.tip; }
    });
    return t;
}

function shorten(id) {
    return /^[0-9a-f]{24,32}$/i.test(id) ? id.slice(0, 6) + '…' + id.slice(-4) : id;
}

// A saturated note color (#red is the common one) needs light text on it,
// otherwise the drill-down links inside the note are unreadable.
function isDark(fill) {
    var m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(fill || ''));
    if (!m) return false;
    var h = m[1].length === 3 ? m[1].replace(/./g, '$&$&') : m[1];
    var r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) < 150;
}

function render(uml, host, opts) {
    opts = opts || {};
    applyTheme(opts.theme || 'light');
    var model = parse(uml);
    var P = model.participants, S = model.steps;
    var onLink = opts.onLinkClick;

    if (opts.compactIds !== false) {
        P.forEach(function (p) {
            if (p.label === p.id) { p.tip = p.tip || p.id; p.label = shorten(p.id); }
        });
    }

    var idx = {};
    P.forEach(function (p, n) { idx[p.id] = n; });

    /* horizontal layout */
    var headW = P.map(function (p) {
        return Math.max(70, measure(p.label, THEME.headFont) + GEOM.headPadX * 2);
    });

    // Notes and self loops hang off the right of their lifeline, so they widen
    // that particular gap rather than every gap.
    var overhang = P.map(function () { return 0; });
    S.forEach(function (s) {
        var n;
        if (s.t === 'note' && s.side !== 'leftof') {
            n = idx[s.id];
            overhang[n] = Math.max(overhang[n], noteWidth(s) + GEOM.noteGap);
        } else if (s.t === 'msg' && s.from && s.from === s.to) {
            n = idx[s.from];
            overhang[n] = Math.max(overhang[n], runsWidth(s.label, THEME.font) + 46);
        }
    });

    // Events arriving from outside start at the left gutter and carry their
    // label above the arrow, so the first lifeline has to sit far enough right
    // for the widest of those labels to fit.
    var offpageW = 0;
    S.forEach(function (s) {
        if (s.t === 'msg' && s.from === null) offpageW = Math.max(offpageW, runsWidth(s.label, THEME.font));
    });

    var x = [], cursor = Math.max(
        GEOM.gutterL + GEOM.offpageX + (headW[0] || 70) / 2,
        GEOM.gutterL + offpageW + 20
    );
    for (var i = 0; i < P.length; i++) {
        if (i > 0) {
            cursor += Math.max(
                headW[i - 1] / 2 + headW[i] / 2 + GEOM.colGap,
                overhang[i - 1] + headW[i] / 2
            );
        }
        x.push(cursor);
    }
    var last = P.length - 1;
    var width = (P.length
        ? x[last] + Math.max(overhang[last], headW[last] / 2)
        : 200) + GEOM.gutterR;

    /* vertical layout */
    var y = GEOM.topPad + GEOM.headH + 14;
    var depth = {}, openBars = [], bars = [], draw = [];

    S.forEach(function (s) {
        var n, k, b;
        if (s.t === 'activate') {
            n = idx[s.id];
            depth[n] = (depth[n] || 0) + 1;
            openBars.push({ n: n, depth: depth[n], y0: y - 6 });
            return;
        }
        if (s.t === 'deactivate') {
            n = idx[s.id];
            for (k = openBars.length - 1; k >= 0; k--) {
                if (openBars[k].n === n) {
                    b = openBars.splice(k, 1)[0];
                    b.y1 = y - 2;
                    bars.push(b);
                    break;
                }
            }
            depth[n] = Math.max(0, (depth[n] || 1) - 1);
            return;
        }
        if (s.t === 'note') {
            var w = noteWidth(s);
            var h = s.rows.length * GEOM.noteLineH + GEOM.notePadY * 2;
            n = idx[s.id];
            var nx = s.side === 'leftof' ? x[n] - GEOM.noteGap - w : x[n] + GEOM.noteGap;
            draw.push({ k: 'note', s: s, x: nx, y: y, w: w, h: h });
            y += h + GEOM.rowGap;
            return;
        }
        if (s.t === 'msg') {
            if (s.from && s.from === s.to) {
                draw.push({ k: 'self', s: s, x: x[idx[s.from]], y: y, d: depth[idx[s.from]] || 0 });
                y += GEOM.selfH;
            } else {
                draw.push({
                    k: 'msg', s: s, y: y,
                    x0: s.from === null ? GEOM.gutterL : x[idx[s.from]],
                    x1: s.outbound ? width - GEOM.gutterR : (s.to === null ? GEOM.gutterL : x[idx[s.to]]),
                    offpage: s.from === null || s.to === null || !!s.outbound
                });
                y += GEOM.msgH;
            }
        }
    });
    openBars.forEach(function (b) { b.y1 = y; bars.push(b); });

    var bodyBottom = y + 6;
    var height = bodyBottom + 6 + GEOM.headH + GEOM.bottomPad;

    /* paint */
    host.innerHTML = '';
    var svg = el('svg', {
        xmlns: SVGNS,
        viewBox: '0 0 ' + Math.ceil(width) + ' ' + Math.ceil(height),
        width: Math.ceil(width), height: Math.ceil(height),
        'class': 'hsm-diagram'
    }, host);
    // The portal's zoom and restore read element.style.height / .width off the
    // svg root, which is where PlantUML's output puts them. Set them inline as
    // well as on the attributes so DebugHsmLog's existing zoom keeps working.
    svg.style.width = Math.ceil(width) + 'px';
    svg.style.height = Math.ceil(height) + 'px';

    var defs = el('defs', {}, svg);
    var marker = el('marker', {
        id: 'hsmArrow', viewBox: '0 0 10 10', refX: 9, refY: 5,
        markerWidth: 7, markerHeight: 7, orient: 'auto-start-reverse'
    }, defs);
    el('path', { d: 'M 0 0 L 10 5 L 0 10 z', fill: 'context-stroke' }, marker);

    var gLife = el('g', {}, svg),
        gBars = el('g', {}, svg),
        gBody = el('g', {}, svg),
        gHead = el('g', { 'class': 'hsm-head' }, svg);

    P.forEach(function (p, n) {
        el('line', {
            x1: x[n], x2: x[n], y1: GEOM.topPad + GEOM.headH, y2: bodyBottom,
            stroke: THEME.lifeline, 'stroke-width': 1, 'stroke-dasharray': '4 4'
        }, gLife);
    });

    bars.forEach(function (b) {
        el('rect', {
            x: x[b.n] - GEOM.actW / 2 + (b.depth - 1) * GEOM.actIndent,
            y: b.y0, width: GEOM.actW, height: Math.max(6, b.y1 - b.y0), rx: 2,
            fill: THEME.activation, stroke: THEME.activationStroke, 'stroke-width': 1
        }, gBars);
    });

    draw.forEach(function (d) {
        var s = d.s, i2;
        if (d.k === 'msg') {
            var my = d.y + 14;
            el('line', {
                x1: d.x0, x2: d.x1, y1: my, y2: my,
                stroke: s.color, 'stroke-width': 1.4,
                'stroke-dasharray': d.offpage ? '5 3' : null,
                'marker-end': 'url(#hsmArrow)'
            }, gBody);
            if (s.from === null) el('circle', { cx: d.x0, cy: my, r: 3, fill: THEME.offpage }, gBody);
            drawRuns(gBody, s.label, (d.x0 + d.x1) / 2, my - 6, THEME.font, THEME.ink, 'middle', onLink);
        } else if (d.k === 'self') {
            var bx = d.x + GEOM.actW / 2 + Math.max(0, d.d - 1) * GEOM.actIndent;
            var top = d.y + 8, bot = d.y + 30, right = bx + 26;
            el('path', {
                d: 'M ' + bx + ' ' + top + ' H ' + right + ' V ' + bot + ' H ' + (bx + 2),
                fill: 'none', stroke: s.color, 'stroke-width': 1.4, 'marker-end': 'url(#hsmArrow)'
            }, gBody);
            drawRuns(gBody, s.label, right + 10, bot - 4, THEME.font, THEME.ink, 'start', onLink);
        } else if (d.k === 'note') {
            var g = el('g', {}, gBody);
            if (s.shape === 'h') {
                var e = GEOM.noteHexIn;
                el('path', {
                    d: 'M ' + (d.x + e) + ' ' + d.y +
                       ' H ' + (d.x + d.w - e) +
                       ' L ' + (d.x + d.w) + ' ' + (d.y + d.h / 2) +
                       ' L ' + (d.x + d.w - e) + ' ' + (d.y + d.h) +
                       ' H ' + (d.x + e) +
                       ' L ' + d.x + ' ' + (d.y + d.h / 2) + ' Z',
                    fill: s.fill, stroke: THEME.noteStroke, 'stroke-width': 1
                }, g);
            } else {
                el('rect', {
                    x: d.x, y: d.y, width: d.w, height: d.h, rx: 4,
                    fill: s.fill, stroke: THEME.noteStroke, 'stroke-width': 1
                }, g);
            }
            // Note text contrast follows the note's own fill, not the page
            // theme: a yellow chip needs dark text on a dark page too.
            var dark = isDark(s.fill);
            var nInk = dark ? '#ffffff' : THEME.noteInk;
            for (i2 = 0; i2 < s.rows.length; i2++) {
                drawRuns(g, s.rows[i2],
                    d.x + GEOM.notePadX + (s.shape === 'h' ? GEOM.noteHexIn : 0),
                    d.y + GEOM.notePadY + 13 + i2 * GEOM.noteLineH,
                    THEME.font, nInk, 'start', onLink,
                    dark ? '#ffffff' : '#0d4bb0');
            }
        }
    });

    P.forEach(function (p, n) {
        [GEOM.topPad, bodyBottom + 6].forEach(function (by) {
            var g = el('g', {}, gHead);
            el('rect', {
                x: x[n] - headW[n] / 2, y: by, width: headW[n], height: GEOM.headH, rx: 6,
                fill: THEME.boxFill, stroke: THEME.boxStroke, 'stroke-width': 1.2
            }, g);
            var runs = p.link ? [{ text: p.label, href: p.link, tip: p.tip }] : [{ text: p.label }];
            var t = drawRuns(g, runs, x[n], by + 22, THEME.headFont, THEME.ink, 'middle', onLink);
            if (p.tip && !p.link) { var ti = el('title', {}, t); ti.textContent = p.tip; }
        });
    });

    return {
        svg: svg, model: model,
        width: width, height: height,
        headerHeight: GEOM.topPad + GEOM.headH
    };
}

export default { render: render, parse: parse };
